import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { tailorsApi, ordersApi } from '../api';
import { useFetch } from '../hooks/useFetch';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useReactToPrint } from 'react-to-print';
import ReceiptTailor from '../components/common/ReceiptTailor';

function AssignModal({ onClose, onSaved }) {

  const [tailors, setTailors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({ orderItemId: '', tailorId: '', laborCost: '', notes: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([tailorsApi.getAll(), ordersApi.getAll({ status: 'pending,stitching', limit: 100 })]).then(([t, o]) => {
      setTailors(t.data.data || []);
      // Flatten order items
      const allOrders = o.data.data?.orders || o.data.data || [];
      const items = [];
      allOrders.forEach((ord) => {

        (ord.items || []).forEach((item) => {
          if (item.status === 'pending') {

            items.push({ ...item, orderNumber: ord.orderNumber, orderId: ord.id });
          }
        });
      });
      setOrders(items);
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    //   Validation
    if (!form.orderItemId) {
      return toast.error("Please select an order item");
    }

    if (!form.tailorId) {
      return toast.error("Please select a tailor");
    }

    if (!form.laborCost) {
      return toast.error("Labor cost is required");
    }

    if (Number(form.laborCost) < 0) {
      return toast.error("Labor cost cannot be negative");
    }
    setLoading(true);
    try {
      await tailorsApi.assign({ ...form, orderItemId: Number(form.orderItemId), tailorId: Number(form.tailorId), laborCost: Number(form.laborCost) });
      toast.success('Tailor assigned!');
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Assign Tailor</h3>
          <button className="btn btn-secondary btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Order Item *</label>
              <select className="form-control" value={form.orderItemId} onChange={(e) => setForm({ ...form, orderItemId: e.target.value })}>
                <option value="">Select Pending Item</option>
                {orders.map((item) => <option key={item.id} value={item.id}>{item.orderNumber} – {item.garmentType}</option>)}
              </select>
            </div>
            <div className="form-row form-row-2">
              <div className="form-group">
                <label className="form-label">Tailor *</label>
                <select className="form-control" value={form.tailorId} onChange={(e) => setForm({ ...form, tailorId: e.target.value })}>
                  <option value="">Select Tailor</option>
                  {tailors.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Labor Cost (₹) *</label>
                <input type="number" className="form-control" step="0.01" min="0" value={form.laborCost} onChange={(e) => setForm({ ...form, laborCost: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Assigning...' : 'Assign Tailor'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Tailors() {
  const [modal, setModal] = useState(false);
  const [tab, setTab] = useState('workload');
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const receiptRef = useRef(null);
  const navigate = useNavigate();

  const { hasRole, user } = useAuth();

  const fetchWorkload = useCallback(() => tailorsApi.getWorkload(), []);
  const fetchAssignments = useCallback(() => tailorsApi.getAssignments({ completed: 'false', limit: 50 }), []);

  // only attempt to load workload when the current user is allowed to see it
  const { data: workload, loading: wLoading, refetch: refetchW } = useFetch(
    fetchWorkload,
    [],
    { immediate: hasRole('admin', 'manager', 'staff') }
  );
  const { data: assignmentData, loading: aLoading, refetch: refetchA } = useFetch(fetchAssignments, []);
  const assignments = assignmentData?.rows || assignmentData || [];

  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    onAfterPrint: () => setSelectedAssignment(null)
  });

  const printReceipt = (assignment) => {
    setSelectedAssignment(assignment);
    setTimeout(() => {
      handlePrint();
    }, 100);
  };

  const viewAssignment = (assignment) => {
    navigate(`/assignments/${assignment.id}`);
  };

  const complete = async (id) => {
    try {
      await tailorsApi.complete(id);
      toast.success('Marked as completed!');
      refetchA();
      if (hasRole('admin', 'manager', 'staff')) refetchW();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Tailor Management</h1>
        {hasRole('admin', 'manager', 'staff') && (
          <button className="btn btn-primary" onClick={() => setModal(true)}>+ Assign Tailor</button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[['workload', 'Workload'], ['assignments', 'Active Assignments']]
          .filter(([key]) => {
            if (key === 'workload') return hasRole('admin', 'manager', 'staff');
            return true;
          })
          .map(([key, label]) => (
            <button key={key} className={`btn ${tab === key ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(key)}>{label}</button>
          ))}
      </div>

      {tab === 'workload' && (
        <div className="grid-3">
          {wLoading ? <div className="empty-state"><div className="spinner" /></div> :
            (workload || []).map((w) => (
              <div key={w.tailor.id} className="card" onClick={() => navigate(`/tailors/${w.tailor.id}`)} style={{ cursor: 'pointer' }}>
                <div className="card-body">
                  <div className="flex items-center gap-3 mb-4">
                    <div style={{ width: 48, height: 48, background: 'linear-gradient(135deg, var(--accent),#8b5cf6)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'white', fontSize: '1.2rem' }}>
                      {w.tailor.name[0]}
                    </div>
                    <div>
                      <div className="font-semibold">{w.tailor.name}</div>
                      <div className="text-xs text-muted">{w.tailor.phone}</div>
                    </div>
                  </div>
                  <div className="grid-2" style={{ gap: '0.75rem' }}>
                    <div style={{ background: 'var(--warning-bg)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 'var(--radius)', padding: '0.75rem', textAlign: 'center' }}>
                      <div className="text-xl font-bold" style={{ color: 'var(--warning)' }}>{w.active}</div>
                      <div className="text-xs text-muted">Active</div>
                    </div>
                    <div style={{ background: 'var(--success-bg)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 'var(--radius)', padding: '0.75rem', textAlign: 'center' }}>
                      <div className="text-xl font-bold" style={{ color: 'var(--success)' }}>{w.completed}</div>
                      <div className="text-xs text-muted">Done</div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <div className="text-xs text-muted">Total Earned</div>
                    <div className="font-bold text-success">₹{Number(w.totalEarned).toLocaleString('en-IN')}</div>
                  </div>
                  {(w.active + w.completed) > 0 && (
                    <div className="mt-3">
                      <div className="progress-bar">
                        <div className="progress-bar-fill" style={{ width: `${w.completed / (w.active + w.completed) * 100}%` }} />
                      </div>
                      <div className="text-xs text-muted mt-1 text-right">{Math.round(w.completed / (w.active + w.completed) * 100)}% completion rate</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          {(!wLoading && (!workload || workload.length === 0)) && (
            <div className="empty-state" style={{ gridColumn: '1/-1' }}><div className="empty-icon">✂️</div><p>No tailors found</p></div>
          )}
        </div>
      )}

      {tab === 'assignments' && (
        <div className="card">
          {aLoading ? <div className="empty-state"><div className="spinner" /></div> : (
            <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
              <table>
                <thead><tr><th>Order</th><th>Garment</th><th>Tailor</th><th>Labor Cost</th><th>Assigned</th><th>Actions</th></tr></thead>
                <tbody>
                  {assignments.length === 0 ? (
                    <tr><td colSpan={6}><div className="empty-state"><div className="empty-icon">🎯</div><p>No active assignments</p></div></td></tr>
                  ) : assignments.map((a) => (
                    <tr key={a.id}>
                      <td className="mono text-sm" style={{ color: 'var(--accent-light)' }}>{a.orderItem?.order?.orderNumber}</td>
                      <td className="font-semibold">{a.orderItem?.garmentType}</td>
                      <td>
                        <div className="font-semibold text-sm">{a.tailor?.name}</div>
                        <div className="text-xs text-muted">{a.tailor?.phone}</div>
                      </td>
                      <td className="font-semibold text-success">₹{Number(a.laborCost).toLocaleString('en-IN')}</td>
                      <td className="text-sm text-muted">{new Date(a.assignedAt).toLocaleDateString('en-IN')}</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn btn-secondary btn-sm" onClick={() => viewAssignment(a)}>View</button>
                          <button className="btn btn-primary btn-sm" onClick={() => printReceipt(a)} title="Print Receipt">🖨️</button>
                          <button className="btn btn-success btn-sm" onClick={() => complete(a.id)}>✓ Complete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {modal && <AssignModal onClose={() => setModal(false)} onSaved={() => { refetchW(); refetchA(); }} />}

      {/* Hidden Receipt Component for Printing */}
      <div style={{ display: 'none' }}>
        <ReceiptTailor ref={receiptRef} assignment={selectedAssignment} />
      </div>
    </div>
  );
}
